/**
 * NewVersionDialog — wraps `PromptEditor` with a full-screen dialog that
 * handles JSON parse, POSTs to `/admin/prompts/:id/versions`, surfaces the
 * 422 VALIDATION_FAILED report inline, and invalidates the TanStack cache on
 * success (Story 7.6 AC2.b).
 *
 * Error surface:
 *   - 400 (schema) — generic toast, user should check the JSON fields.
 *   - 422 (validator) — render blockers + warnings as a list under the form;
 *                       the form stays OPEN so the operator can fix.
 *   - 5xx / network  — toast.
 */

import { useMutation } from '@tanstack/react-query';
import * as React from 'react';
import { toast } from 'sonner';
import { AlertTriangle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ApiError, apiClient } from '@/lib/api';
import type {
  PromptVersion,
  ValidationFailedResponse,
  ValidationReport,
} from '@/lib/prompts-types';
import { PromptEditor, type PromptEditorValue, validateEditor } from './PromptEditor';

export interface NewVersionDialogProps {
  promptId: string;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onCreated: (version: PromptVersion) => void;
  /**
   * Optional seed for the form (e.g. forked from an existing version). When
   * omitted, the editor opens empty so operators can author from scratch.
   */
  initialValue?: PromptEditorValue;
}

const EMPTY_VALUE: PromptEditorValue = {
  systemPrompt: '',
  fewShotsText: '[]',
  jsonSchemaText: '{}',
  changeSummary: '',
};

export function NewVersionDialog({
  promptId,
  open,
  onOpenChange,
  onCreated,
  initialValue,
}: NewVersionDialogProps) {
  const [value, setValue] = React.useState<PromptEditorValue>(initialValue ?? EMPTY_VALUE);
  const [report, setReport] = React.useState<ValidationReport | null>(null);

  React.useEffect(() => {
    if (open) {
      setValue(initialValue ?? EMPTY_VALUE);
      setReport(null);
    }
  }, [open, initialValue]);

  const mutation = useMutation({
    mutationFn: (payload: {
      systemPrompt: string;
      fewShots: unknown[];
      jsonSchema: unknown;
      changeSummary?: string;
    }) => apiClient.post<PromptVersion>(`/admin/prompts/${promptId}/versions`, payload),
    onSuccess: (version) => {
      toast.success(`Versão v${version.versionNumber} criada.`);
      setReport(null);
      onCreated(version);
      onOpenChange(false);
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 422) {
        const body = err.body as ValidationFailedResponse | undefined;
        if (body?.error?.report) {
          setReport(body.error.report);
          toast.error('Validação bloqueou a nova versão. Veja os problemas abaixo.');
          return;
        }
      }
      if (err instanceof ApiError && err.status === 400) {
        toast.error('Dados inválidos. Verifique os campos JSON.');
        return;
      }
      toast.error('Não foi possível criar a versão.');
    },
  });

  const editorErrors = validateEditor(value);
  const hasEditorError = Boolean(editorErrors.fewShots) || Boolean(editorErrors.jsonSchema);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (!value.systemPrompt.trim()) {
      toast.error('System prompt é obrigatório.');
      return;
    }
    if (hasEditorError) {
      toast.error('Corrija os erros de JSON antes de salvar.');
      return;
    }

    const fewShotsTrim = value.fewShotsText.trim();
    const schemaTrim = value.jsonSchemaText.trim();

    // Safe to parse: `validateEditor` above has already asserted the shape
    // so `JSON.parse` won't throw here (errors are surfaced in the UI).
    const fewShots = fewShotsTrim ? (JSON.parse(fewShotsTrim) as unknown[]) : [];
    const jsonSchema = schemaTrim ? JSON.parse(schemaTrim) : {};

    mutation.mutate({
      systemPrompt: value.systemPrompt,
      fewShots,
      jsonSchema,
      changeSummary: value.changeSummary.trim() || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] w-[calc(100vw-2rem)] flex-col gap-4 overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Nova versão</DialogTitle>
          <DialogDescription>
            A versão é criada como rascunho e exige ativação para entrar em produção. Conversas em
            andamento não são afetadas.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="max-h-[60vh] overflow-y-auto pr-2">
            <PromptEditor value={value} onChange={setValue} disabled={mutation.isPending} />
          </div>

          {report && (
            <div
              className="mt-4 space-y-2 rounded-md border border-destructive/30 bg-destructive/5 p-3"
              role="alert"
            >
              <div className="flex items-center gap-2 text-sm font-medium text-destructive">
                <AlertTriangle className="size-4" /> Validação bloqueou esta versão
              </div>
              <ul className="space-y-1 text-xs">
                {report.blockers.map((b, idx) => (
                  <li key={`b-${idx}`} className="text-destructive">
                    [{b.severity}] <span className="font-mono">{b.name}</span>
                    {b.message ? ` — ${b.message}` : ''}
                  </li>
                ))}
                {report.warnings.map((w, idx) => (
                  <li key={`w-${idx}`} className="text-amber-600 dark:text-amber-400">
                    [{w.severity}] <span className="font-mono">{w.name}</span>
                    {w.message ? ` — ${w.message}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending || hasEditorError}>
              {mutation.isPending ? 'Salvando…' : 'Salvar versão'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default NewVersionDialog;
